<script setup lang="ts">
import { ref } from 'vue'
import {
    useCheckout,
    usePayment,
    useCart,
    useUser,
    useNotification,
    useState,
    useI18n,
} from '#imports'
import { useMollie, NEW_CARD } from '~/composables/useMollie'
import { formValidation } from '@/utils/formValidation'

const props = defineProps<{
    form: HTMLFormElement | null
    customerComment?: string
}>()

const emit = defineEmits(['update:selectStep', 'onError', 'orderPlaced'])

const { validateForm } = formValidation()
const { createOrder } = useCheckout()
const { handlePayment } = usePayment()
const { refreshCart } = useCart()
const { user } = useUser()
const { showNotification } = useNotification()
const { mollie, shouldSaveCard, selectedMandateId } = useMollie()
const { t } = useI18n()

const loading = ref(false)
const showOverlay = useState<boolean>('checkout-overlay', () => false)

function selectStep(step: string) {
    emit('update:selectStep', step)
}

async function onSubmit() {
    if (!props.form) return
    const isValid = await validateForm(props.form)
    if (!isValid) return

    const mollieInstance = mollie.value
    if (!mollieInstance) {
        showNotification('Mollie SDK not initialised', 'error', true)
        return
    }

    // A saved card (mandate) is reused as a recurring-sequence payment: `mandateId` alone
    // rides the /handle-payment body and no new token is minted. Anything else means the
    // shopper is entering a fresh card and we tokenise as before (ADR-0006 / A4G-3141).
    const reuseMandateId = selectedMandateId.value !== NEW_CARD ? selectedMandateId.value : null

    try {
        loading.value = true
        showOverlay.value = true

        const customerId = user.value?.id
        if (!customerId) {
            showNotification('Customer not signed in', 'error', true)
            loading.value = false
            showOverlay.value = false
            return
        }

        // Build the payment-body fields once, branching on saved-card reuse vs. new card.
        let paymentBody: { creditCardToken?: string; savePaymentDetails?: boolean; mandateId?: string }
        if (reuseMandateId) {
            paymentBody = { mandateId: reuseMandateId }
        } else {
            const tokenResult = await mollieInstance.createToken()
            if (tokenResult.error || !tokenResult.token) {
                showNotification(t('checkout.mollie.tokenError'), 'error', true)
                selectStep('payment')
                loading.value = false
                showOverlay.value = false
                return
            }
            // Pass the one-shot Mollie Components token inline in the /handle-payment body as
            // `creditCardToken`, where the gateway's card handler reads it. `savePaymentDetails`
            // reflects the "save this card" opt-in — when true the gateway mints a saved card
            // (mandate) as a side-effect of this successful first payment.
            paymentBody = {
                creditCardToken: tokenResult.token,
                savePaymentDetails: shouldSaveCard.value,
            }
        }

        const order = await createOrder({ customerComment: props.customerComment })
        const { redirectUrl } = await handlePayment(order.id, paymentBody)

        if (redirectUrl) {
            emit('orderPlaced', order.id)
            window.location.href = redirectUrl
        } else {
            await refreshCart()
        }
    } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e)
        showNotification(message, 'error', true)
        if (message.includes('payment-method-blocked')) {
            emit('onError', 'payment-method-blocked')
        }
        loading.value = false
        showOverlay.value = false
    }
}
</script>

<template>
    <div>
        <Teleport to="#checkoutNavigation">
            <nav class="navigation flex flex-col flex-wrap justify-between items-center gap-2 lg:flex-row lg:flex-nowrap lg:items-center">
                <div
                    class="btn btn-lg btn-tertiary w-full order-2 lg:order-1 lg:w-auto lg:btn-sm"
                    role="button"
                    tabindex="0"
                    @click="selectStep('payment')"
                    @keyup.enter="selectStep('payment')"
                >
                    <MiscIcon name="chevron-left" />
                    {{ $t('checkout.summary.navigationBack') }}
                </div>
                <div class="w-full flex flex-col items-end gap-2 order-1 lg:w-auto lg:order-2">
                    <button
                        type="submit"
                        :disabled="loading"
                        class="btn btn-lg btn-accent w-full lg:w-auto"
                        data-cy="checkout-navigation-place-order"
                        @click.prevent="onSubmit()"
                    >
                        <span v-if="loading" class="loading" />
                        <MiscIcon v-else name="check" />
                        {{ $t('checkout.summary.navigationPlaceOrder') }}
                    </button>
                    <div class="gap-1 items-center mt-3 text-base-content text-xs font-normal flex lg:hidden">
                        <MiscIcon name="lock" size="xs" />
                        {{ $t('cart.transaction') }}
                        <div class="font-bold underline lowercase">
                            {{ $t('cart.safe') }}
                        </div>
                    </div>
                </div>
            </nav>
        </Teleport>
    </div>
</template>
